import { useState } from "react";
import { format } from "date-fns";
import { es as esLocale } from "date-fns/locale";
import { CalendarDays, CheckCircle2, ImagePlus, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { resizeImage } from "@/lib/image";
import { useLang } from "@/lib/i18n";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const EMPTY = { title: "", venue: "", description: "", whatsapp: "", info_url: "", website: "" };
const ymd = (d) => format(d, "yyyy-MM-dd");

const DatePicker = ({ range, setRange }) => {
  const { t, lang } = useLang();
  const loc = lang === "es" ? { locale: esLocale } : {};
  const label = range?.from ? `${format(range.from, "PP", loc)}${range.to ? ` – ${format(range.to, "PP", loc)}` : ""}` : t("retreat.pickDates");
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={`flex h-10 w-full items-center gap-2 rounded-md border border-input bg-white px-3 text-left text-sm ${range?.from ? "text-ink" : "text-moss"}`} data-testid="host-dates">
          <CalendarDays className="h-4 w-4 text-sage" /> {label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="range" selected={range} onSelect={setRange} disabled={{ before: new Date() }} numberOfMonths={1} initialFocus {...loc} data-testid="host-calendar" />
      </PopoverContent>
    </Popover>
  );
};

const PhotoPicker = ({ preview, onFile }) => {
  const { t } = useLang();
  return (
    <label className="flex cursor-pointer items-center gap-4 rounded-xl border-2 border-dashed border-clay bg-white p-4 transition-colors duration-200 hover:border-forest" data-testid="host-photo">
      {preview ? <img src={preview} alt="" className="h-20 w-28 rounded-lg object-cover" /> : <span className="grid h-20 w-28 place-items-center rounded-lg bg-linen text-sage"><ImagePlus className="h-6 w-6" /></span>}
      <span className="text-sm font-semibold text-moss">{preview ? t("retreat.changePhoto") : t("retreat.photo")}</span>
      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} data-testid="host-photo-input" />
    </label>
  );
};

export const RetreatHostForm = () => {
  const { t } = useLang();
  const [f, setF] = useState(EMPTY);
  const [range, setRange] = useState();
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pick = async (file) => {
    try {
      const small = await resizeImage(file);
      setPhoto({ file: small, preview: URL.createObjectURL(small) });
    } catch {
      setError(t("retreat.badPhoto"));
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!photo) return setError(t("retreat.needPhoto"));
    if (!range?.from) return setError(t("retreat.needDates"));
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("start_date", ymd(range.from));
    fd.append("end_date", ymd(range.to || range.from));
    fd.append("image", photo.file);
    setBusy(true);
    try {
      await api.post("/retreats/submit", fd);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  if (done) {
    return (
      <div className="rounded-2xl border border-clay bg-white p-8 text-center" data-testid="host-success">
        <CheckCircle2 className="mx-auto h-10 w-10 text-forest" />
        <p className="mt-4 font-display text-2xl font-black text-forest">{t("retreat.thanksTitle")}</p>
        <p className="mt-2 text-moss">{t("retreat.thanksText")}</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-4 rounded-2xl border border-clay bg-white p-6 sm:p-8" data-testid="host-form">
      <div className="space-y-1.5"><Label className="font-bold">{t("retreat.fName")}</Label><Input required maxLength={120} value={f.title} onChange={set("title")} data-testid="host-title" /></div>
      <PhotoPicker preview={photo?.preview} onFile={pick} />
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5"><Label className="font-bold">{t("retreat.fDates")}</Label><DatePicker range={range} setRange={setRange} /></div>
        <div className="space-y-1.5"><Label className="font-bold">{t("retreat.fVenue")}</Label><Input required maxLength={200} value={f.venue} onChange={set("venue")} data-testid="host-venue" /></div>
        <div className="space-y-1.5"><Label className="font-bold">{t("retreat.fWhatsapp")}</Label><Input required type="tel" placeholder="+505 8888 8888" value={f.whatsapp} onChange={set("whatsapp")} data-testid="host-whatsapp" /></div>
        <div className="space-y-1.5"><Label className="font-bold">{t("retreat.fUrl")}</Label><Input type="text" inputMode="url" placeholder="https://" value={f.info_url} onChange={set("info_url")} data-testid="host-url" /></div>
      </div>
      <div className="space-y-1.5"><Label className="font-bold">{t("retreat.fDesc")}</Label><Textarea required minLength={10} maxLength={1500} rows={4} value={f.description} onChange={set("description")} data-testid="host-description" /></div>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
      {error && <p className="text-sm font-semibold text-destructive" data-testid="host-error">{error}</p>}
      <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="host-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("retreat.submit")}</button>
      <p className="text-xs text-moss">{t("retreat.reviewNote")}</p>
    </form>
  );
};
